import { memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { colors } from 'src/theme'
import { HorizontalDotsMinor, MinusMinor, TickMinor } from '@shopify/polaris-icons'
import { CustomerCollectedDataStatus } from 'src/constants/customer/enums'
import { customerCollectedDataStatusTitle } from 'src/constants/customer/constants'
import { GSIcon } from 'src/components/shared/GSIcon'

interface Props {
  status: CustomerCollectedDataStatus
  expiresSoon?: boolean
}

const expiresSoonStatus = {
  color: colors.COLORS_ORANGE_500,
  text: 'Expires soon',
  icon: (
    <GSIcon iconName="gs_triangle_exclamation" css={tw`[&>path]:stroke-[rgba(255, 99, 32, 1)]`} />
  ),
}

const stepStatusMap = {
  [CustomerCollectedDataStatus.Completed]: {
    color: colors.COLORS_GREEN_500,
    text: customerCollectedDataStatusTitle[CustomerCollectedDataStatus.Completed],
    icon: <TickMinor width={20} fill={colors.COLORS_GREEN_500} />,
  },
  [CustomerCollectedDataStatus.Created]: {
    color: colors.SHADES_GREY_300,
    text: customerCollectedDataStatusTitle[CustomerCollectedDataStatus.Created],
    icon: <MinusMinor width={20} fill={colors.SHADES_GREY_300} />,
  },
  [CustomerCollectedDataStatus.Failed]: {
    color: colors.COLORS_RED_500,
    text: customerCollectedDataStatusTitle[CustomerCollectedDataStatus.Failed],
    icon: <HorizontalDotsMinor width={20} fill={colors.COLORS_RED_500} />,
  },
  [CustomerCollectedDataStatus.WaitingForInternalApproval]: {
    color: colors.SHADES_GREY_300,
    text: customerCollectedDataStatusTitle[CustomerCollectedDataStatus.WaitingForInternalApproval],
    icon: <HorizontalDotsMinor width={20} fill={colors.SHADES_GREY_300} />,
  },
  [CustomerCollectedDataStatus.InProgress]: {
    color: colors.COLORS_BLUE_500,
    text: customerCollectedDataStatusTitle[CustomerCollectedDataStatus.InProgress],
    icon: <HorizontalDotsMinor width={20} fill={colors.COLORS_BLUE_500} />,
  },
  [CustomerCollectedDataStatus.Archived]: {
    color: colors.SHADES_GREY_300,
    text: customerCollectedDataStatusTitle[CustomerCollectedDataStatus.Archived],
    icon: <HorizontalDotsMinor width={20} fill={colors.SHADES_GREY_300} />,
  },
  [CustomerCollectedDataStatus.Expired]: {
    color: colors.COLORS_RED_500,
    text: customerCollectedDataStatusTitle[CustomerCollectedDataStatus.Expired],
    icon: (
      <GSIcon iconName="gs_triangle_exclamation" css={tw`[&>path]:stroke-[rgba(255, 26, 81, 1)]`} />
    ),
  },
}

const CustomerStepStatusComponent = ({ status, expiresSoon }: Props) => {
  const params = expiresSoon ? expiresSoonStatus : stepStatusMap[status]

  if (!params) {
    return null
  }

  return (
    <div css={tw`flex items-center`}>
      {params.icon}
      <Typography css={tw`ml-1`} variant="body2" color={params.color}>
        {params.text}
      </Typography>
    </div>
  )
}

export const CustomerStepStatus = memo(CustomerStepStatusComponent)
